'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { Star, Heart, ArrowRight, ChevronLeft, ChevronRight, Gamepad2, Monitor } from 'lucide-react';
import type { CatalogItem } from '@/types/catalog';
import { usePriceConvert } from '@/lib/PriceContext';
import { useT } from '@/hooks/useT';
import type { I18nKey } from '@/lib/i18n';

const AUTO_DELAY = 8000;

const PLAT: Record<string, { label: string; Icon: typeof Gamepad2; cls: string }> = {
  XBOXSERIESX: { label: 'Series X|S', Icon: Gamepad2, cls: 'ptag-xsx'  },
  XBOXONE:     { label: 'Xbox One',   Icon: Gamepad2, cls: 'ptag-xone' },
  PC:          { label: 'PC',         Icon: Monitor,  cls: 'ptag-pc'   },
};

/* Category → localized label i18n key (mirrors GenreGrid's key set). */
const CATEGORY_KEY: Record<string, I18nKey> = {
  'Action & adventure': 'genre_action',
  'Shooter':            'genre_shooter',
  'Role playing':       'genre_rpg',
  'Racing & flying':    'genre_racing',
  'Sports':             'genre_sports',
  'Fighting':           'genre_fighting',
  'Simulation':         'genre_simulation',
  'Strategy':           'genre_strategy',
  'Puzzle & trivia':    'genre_puzzle',
  'Platformer':         'genre_platformer',
  'Family & kids':      'genre_family',
  'Music':              'genre_music',
};

function fmt(price: number, currency: string) {
  return new Intl.NumberFormat('uk-UA', {
    style: 'currency', currency: currency || 'USD',
  }).format(price);
}

function useWishlist(productId: string) {
  const [inList, setInList] = useState(false);
  useEffect(() => {
    const sync = () => {
      try {
        const list: string[] = JSON.parse(localStorage.getItem('wishlist') || '[]');
        setInList(list.includes(productId));
      } catch { /* noop */ }
    };
    sync();
    window.addEventListener('wishlist-updated', sync);
    return () => window.removeEventListener('wishlist-updated', sync);
  }, [productId]);
  const toggle = useCallback((e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const list: string[] = JSON.parse(localStorage.getItem('wishlist') || '[]');
      const next = list.includes(productId) ? list.filter(id => id !== productId) : [...list, productId];
      localStorage.setItem('wishlist', JSON.stringify(next));
      window.dispatchEvent(new Event('wishlist-updated'));
    } catch { /* noop */ }
  }, [productId]);
  return { inList, toggle };
}

function WishBtn({ productId }: { productId: string }) {
  const { inList, toggle } = useWishlist(productId);
  const t = useT();
  return (
    <button
      className={`v4hero-wish${inList ? ' v4hero-wish--on' : ''}`}
      onClick={toggle}
      aria-label={inList ? t('hero_wishlist_remove') : t('hero_wishlist_add')}
    >
      <Heart size={18} fill={inList ? 'currentColor' : 'none'} strokeWidth={inList ? 0 : 1.8} />
    </button>
  );
}

export function HeroSlider({ items }: { items: CatalogItem[] }) {
  // Єдина конвертація через displayRates (як каталог/PDP) — щоб курс збігався всюди.
  const { convert, lang } = usePriceConvert();
  const t = useT();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [prog, setProg] = useState(0);

  const goTo = useCallback((i: number) => {
    setActive(i);
    setProg(k => k + 1);
  }, []);

  const next = useCallback(() => goTo((active + 1) % items.length), [active, goTo, items.length]);
  const prev = useCallback(() => goTo((active - 1 + items.length) % items.length), [active, goTo, items.length]);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const id = setTimeout(next, AUTO_DELAY);
    return () => clearTimeout(id);
  }, [paused, next, active, prog, items.length]);

  const touchStartX = useRef<number | null>(null);

  if (!items.length) return null;
  const item = items[active];
  const discount = item.price.discount_pct ? Math.round(item.price.discount_pct) : null;
  const plats = item.platforms.filter(p => PLAT[p]).slice(0, 3);
  const artImg = item.titled_hero_url;
  const posterImg = item.poster_image_url_w400 ?? item.poster_image_url;

  return (
    <section
      className="v4hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
      onTouchEnd={e => {
        if (touchStartX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(dx) > 50) dx < 0 ? next() : prev();
        touchStartX.current = null;
      }}
      aria-label={t('hero_featured')}
    >
      {/* -- Background slides -- */}
      {items.map((it, i) => (
        <div
          key={it.product_id}
          className={`v4hero-slide${i === active ? ' active' : ''}`}
          aria-hidden={i !== active}
        >
          {it.hero_image_url
            ? <img src={it.hero_image_url} alt="" className="v4hero-img" loading={i === 0 ? 'eager' : 'lazy'} />
            : <div className="v4hero-img-fallback" />
          }
        </div>
      ))}

      {/* -- Overlays -- */}
      <div className="v4hero-ov v4hero-ov--glow" />
      <div className="v4hero-ov v4hero-ov--l" />
      <div className="v4hero-ov v4hero-ov--b" />

      {/* -- Inner: text + art -- */}
      <div className="v4hero-inner">

        <div className="v4hero-body">
          <div className="v4hero-meta">
            {item.category && (
              <span className="v4hero-cat">
                {CATEGORY_KEY[item.category] ? t(CATEGORY_KEY[item.category]) : item.category}
              </span>
            )}
            {item.rating_all_time > 0 && (
              <span className="v4hero-rating">
                <Star size={10} fill="currentColor" strokeWidth={0} />
                {item.rating_all_time.toFixed(1)}
              </span>
            )}
            {plats.map(p => {
              const { label, Icon, cls } = PLAT[p];
              return (
                <span key={p} className={`v4hero-plat ${cls}`}>
                  <Icon size={10} strokeWidth={2} /> {label}
                </span>
              );
            })}
          </div>

          <h1 className="v4hero-title">
            {lang === 'ru' && item.title_ru ? item.title_ru
              : lang === 'uk' && item.title_uk ? item.title_uk
              : item.title}
          </h1>

          {/* Always render the desc slot (empty string keeps its reserved
              height) so the price row + actions never jump between slides. */}
          <p className="v4hero-desc">{item.short_description ?? ''}</p>

          <div className="v4hero-price-row">
            {discount && discount > 0 && <span className="v4hero-disc">−{discount}%</span>}
            {(item.min_key_usd ?? item.price.current) != null && (
              <span className="v4hero-price">
                {convert((item.min_key_usd ?? item.price.current)!) ?? fmt((item.min_key_usd ?? item.price.current)!, item.price.currency)}
              </span>
            )}
            {item.price.msrp != null && item.price.msrp > (item.price.current ?? 0) && (
              <span className="v4hero-msrp">
                {convert(item.price.msrp) ?? fmt(item.price.msrp, item.price.currency)}
              </span>
            )}
          </div>

          <div className="v4hero-actions">
            <Link href={`/product/${item.product_id}`} className="v4hero-cta">
              {t('hero_more')} <ArrowRight size={15} strokeWidth={2.2} />
            </Link>
            <WishBtn productId={item.product_id} />
          </div>
        </div>

        {/* Art — key={active} restarts animation on slide change */}
        {artImg && (
          <div key={active} className="v4hero-art">
            <picture>
              {posterImg && <source media="(max-width: 700px)" srcSet={posterImg} />}
              <img src={artImg} alt={item.title} className="v4hero-art-img" loading="eager" />
            </picture>
          </div>
        )}
      </div>

      {/* -- Arrows -- */}
      {items.length > 1 && (
        <>
          <button className="v4hero-arrow v4hero-arrow--l" onClick={prev} aria-label={t('hero_prev')}>
            <ChevronLeft size={20} />
          </button>
          <button className="v4hero-arrow v4hero-arrow--r" onClick={next} aria-label={t('hero_next')}>
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* -- Dots -- */}
      {items.length > 1 && (
        <div className="v4hero-dots" role="tablist">
          {items.map((_, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === active}
              className={`v4hero-dot${i === active ? ' active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`${t('hero_featured')} ${i + 1}`}
            >
              {i === active && (
                <span
                  key={prog}
                  className="v4hero-dot-progress"
                  style={{ '--dur': `${AUTO_DELAY}ms` } as React.CSSProperties}
                />
              )}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
